import { useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type { MigrationProject } from '../types';

interface PlannedDateControlProps {
  /** `null` pour une école « à faire » sans projet encore créé. */
  projectId: number | null;
  /** Requis quand `projectId` est null : un projet (brouillon) est créé à la volée. */
  schoolId?: number;
  /** Date planifiée actuelle (AAAA-MM-JJ) ou null. */
  plannedMigrationDate: string | null;
  /** Appelé avec le projet renvoyé par l'API après mise à jour. */
  onUpdated: (project: MigrationProject) => void;
  /** Notifié en cas d'échec (message géré par le parent). */
  onError?: (message: string) => void;
  className?: string;
  inputClassName?: string;
}

/**
 * Sélecteur de la date de migration prévue d'un projet. À n'afficher que pour
 * un administrateur — le parent est responsable du gating (les non-admins
 * voient un affichage en lecture seule). Par défaut la date du jour est
 * proposée dans le champ ; l'école « à faire » sans projet matérialise un
 * brouillon à la première saisie (comme l'assignation d'un responsable).
 */
export function PlannedDateControl({
  projectId,
  schoolId,
  plannedMigrationDate,
  onUpdated,
  onError,
  className,
  inputClassName,
}: PlannedDateControlProps) {
  const [saving, setSaving] = useState(false);

  // Pas de date par défaut : le champ reste vide (placeholder jj/mm/aaaa) tant
  // qu'aucune date n'a été saisie — on n'impose jamais aujourd'hui.
  const value = plannedMigrationDate ?? '';

  const handleChange = async (next: string) => {
    const nextDate = next === '' ? null : next;
    if ((plannedMigrationDate ?? null) === nextDate) return;

    try {
      setSaving(true);
      let pid = projectId;
      if (pid == null) {
        // École « à faire » : effacer une date sur une école sans projet est
        // un no-op ; sinon on matérialise un projet (brouillon) pour la porter.
        if (nextDate == null) return;
        if (schoolId == null) {
          onError?.('Impossible de créer le projet de migration');
          return;
        }
        const created = await migrationV1V2Api.createProject({ schoolId });
        pid = created.id;
      }
      const project = await migrationV1V2Api.updateProjectPlannedDate(pid, nextDate);
      onUpdated(project);
    } catch {
      onError?.('Échec de la mise à jour de la date de migration prévue');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cn('relative inline-flex items-center', className)}>
      <Input
        type="date"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        aria-label="Date de migration prévue"
        className={cn(saving && 'opacity-60 pointer-events-none', inputClassName)}
      />
      {plannedMigrationDate && !saving && (
        <button
          type="button"
          onClick={() => handleChange('')}
          aria-label="Effacer la date de migration prévue"
          title="Effacer la date"
          className="ml-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-700 dark:hover:text-gray-200"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {saving && (
        <Loader2 className="pointer-events-none absolute right-2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}
